import React, { useState, useEffect } from "react";

const NPSCalculator = () => {
  const [monthlyContribution, setMonthlyContribution] = useState(5000);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
    monthlyPension: "0.00",
    lumpsum: "0.00",
  });

  // NPS Calculation Function
  const calculateNPS = () => {
    const P = parseFloat(monthlyContribution);
    const r = parseFloat(expectedReturn) / 100 / 12; // Monthly rate
    const age = parseFloat(currentAge);
    const retirement = parseFloat(retirementAge);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(age) || isNaN(retirement) || P <= 0 || retirement <= age) return;

    const years = retirement - age;
    const n = years * 12; // Total months

    // NPS Future Value calculation using compound interest formula
    // FV = P × [((1 + r)^n - 1) / r] × (1 + r)
    const futureValue = P * (Math.pow(1 + r, n) - 1) / r * (1 + r);
    
    const totalInvestment = P * n;
    const interestEarned = futureValue - totalInvestment;
    
    // At retirement, 60% must be used to purchase annuity, 40% can be withdrawn as lumpsum
    const annuityAmount = futureValue * 0.6;
    const lumpsumAmount = futureValue * 0.4;
    
    // Assuming annuity rate of 6% per annum
    const annuityRate = 0.06;
    const monthlyPension = (annuityAmount * annuityRate) / 12;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = futureValue / Math.pow(1 + inflation, years);

    setResult({
      futureValue: futureValue.toFixed(2),
      totalInvestment: totalInvestment.toFixed(2),
      estimatedReturns: interestEarned.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
      monthlyPension: monthlyPension.toFixed(2),
      lumpsum: lumpsumAmount.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateNPS();
  }, []);

  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  const tenure = retirementAge - currentAge;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1> National Pension System Calculator</h1>
          <p>
            Calculate your National Pension System corpus and estimate your monthly pension after retirement
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                NPS Details
              </h2>

              {/* Monthly Contribution */}
              <div className="input-group">
                <label>Monthly Contribution</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlyContribution}
                    onChange={(e) => setMonthlyContribution(e.target.value)}
                    className="calc-input"
                    placeholder="5000"
                  />
                </div>
                <div className="quick-select-group">
                  {[2000, 5000, 10000, 15000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlyContribution(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected Annual Return */}
              <div className="input-group">
                <label>Expected Annual Return</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 14].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setExpectedReturn(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Age */}
              <div className="input-group">
                <label>Current Age</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={currentAge}
                    onChange={(e) => setCurrentAge(e.target.value)}
                    className="calc-input"
                    placeholder="30"
                  />
                </div>
                <div className="quick-select-group">
                  {[25, 30, 35, 40].map((age) => (
                    <button
                      key={age}
                      onClick={() => setCurrentAge(age)}
                      className="quick-select-btn success"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Retirement Age */}
              <div className="input-group">
                <label>Retirement Age</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={retirementAge}
                    onChange={(e) => setRetirementAge(e.target.value)}
                    className="calc-input"
                    placeholder="60"
                  />
                </div>
                <div className="quick-select-group">
                  {[60, 65, 70, 75].map((age) => (
                    <button
                      key={age}
                      onClick={() => setRetirementAge(age)}
                      className="quick-select-btn success"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateNPS} className="calculate-btn">
                Calculate NPS Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                NPS Summary
              </h2>

              <div className="result-card">
                {/* Total Investment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Investment</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Wealth Gain */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Wealth Gain</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth
                  </div>
                </div>

                {/* Total Corpus - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Corpus at {retirementAge}</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {tenure} years at {expectedReturn}% return
                  </div>
                </div>

                {/* Monthly Pension */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Est. Monthly Pension</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyPension).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    From 60% annuity at 6% rate
                  </div>
                </div>

                {/* Lumpsum Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Lumpsum Withdrawal (40%)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.lumpsum).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Tax-free withdrawal at retirement
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">NPS Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Investment</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Wealth Gain</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the NPS Calculator Work?</h3>
              <p>
                The NPS calculator estimates your retirement corpus by calculating compound returns on 
                your monthly contributions. At retirement, 60% of the corpus must be used to purchase 
                an annuity (which provides monthly pension), while 40% can be withdrawn as a tax-free 
                lumpsum. This calculator helps you plan your retirement by showing expected pension 
                income and total corpus based on your investment pattern.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              FV = P × [((1 + r)^n - 1) / r] × (1 + r)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">FV</div>
                <div className="formula-param-value">Future Value (Corpus)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Monthly Contribution</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly Return Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Investment Period (Months)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is National Pension System (NPS)?</h5>
              <p>
                NPS is a government-sponsored voluntary retirement savings scheme designed to provide 
                pension income after retirement. It's open to all Indian citizens aged 18-70 years. 
                You can contribute regularly during your working life, and the accumulated corpus is 
                used to purchase an annuity at retirement that provides monthly pension. NPS offers 
                market-linked returns and additional tax benefits under Section 80CCD(1B).
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the tax benefits of NPS?</h5>
              <p>
                NPS offers triple tax benefits - contributions up to ₹1.5 lakh qualify for deduction 
                under Section 80C, plus an additional ₹50,000 deduction under Section 80CCD(1B). 
                Employer contributions up to 10% of salary are tax-free under Section 80CCD(2). The 
                40% lumpsum withdrawal at retirement is completely tax-exempt. However, the 60% 
                annuity amount and pension received are taxable as per your income slab.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I withdraw my NPS before retirement?</h5>
              <p>
                Premature exit is allowed after 3 years from the account opening date, but 80% of 
                the corpus must be used to purchase an annuity, and only 20% can be withdrawn as 
                lumpsum. Partial withdrawals are permitted for specific reasons like children's 
                education, marriage, or house purchase after 3 years (limited to 25% of contributions, 
                maximum 3 times during the entire tenure). At normal retirement (age 60), you get 
                40% lumpsum and 60% annuity.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the different NPS account types?</h5>
              <p>
                NPS has two tiers - Tier I is the main pension account with lock-in until retirement 
                and offers tax benefits. Tier II is a voluntary savings account with complete 
                liquidity (withdraw anytime) but no tax benefits. Tier I has minimum annual 
                contribution of ₹1,000 while Tier II has ₹250 minimum per contribution. You must 
                have a Tier I account to open Tier II. Tier I is mandatory; Tier II is optional.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I choose between Active and Auto Choice in NPS?</h5>
              <p>
                In Active Choice, you decide asset allocation across Equity (up to 75%), Corporate 
                Bonds, and Government Securities. In Auto Choice (Life Cycle Fund), allocation 
                automatically shifts from equity to debt as you age, reducing risk near retirement. 
                Auto Choice has three options - Aggressive (up to 75% equity), Moderate (up to 50% 
                equity), and Conservative (up to 25% equity). Beginners usually prefer Auto Choice 
                for its simplicity and automatic rebalancing.
              </p>
            </div>

            <div className="faq-item">
              <h5>What returns can I expect from NPS?</h5>
              <p>
                NPS returns are market-linked and vary based on your asset allocation. Historically, 
                equity funds have given 9-12% returns, while debt funds give 8-10%. Your actual 
                returns depend on market performance and fund manager efficiency. NPS has very low 
                charges (0.01% for Tier I), making it one of the most cost-effective retirement 
                products. The calculator assumes average returns, but actual returns will vary 
                year-to-year based on market conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSCalculator;